import { LANE_PAGE_SIZE, lookaheadHasMore } from '../../boardWindowing';

// The pure half of a My Work board lane's paging, kept apart from the hook so it can be tested without React.
// As on the ticket board: a page is pageSize cards - LANE_PAGE_SIZE on desktop, PHONE_PAGE_SIZE on a phone - asked
// for with one extra to tell whether there are more.

export const pageRequest = (skip: number, pageSize = LANE_PAGE_SIZE): { skip: number; top: number } => ({ skip, top: pageSize + 1 });

// size is one page; a Refresh takes back as many as were loaded (refreshRequest).
export const takePage = <T>(raw: T[], size = LANE_PAGE_SIZE): { page: T[]; hasMore: boolean } => ({
  page: raw.slice(0, size),
  hasMore: lookaheadHasMore(raw.length, size),
});

// Refresh, as the ticket board's (refreshWindowParams): every card already loaded comes back in one request - at
// least a page - plus one to tell whether there are more, so the lane is replaced in place and keeps its scroll
// position rather than shrinking to one page and jumping.
export const refreshRequest = (loaded: number, pageSize = LANE_PAGE_SIZE): { skip: number; top: number; size: number } => {
  const size = Math.max(pageSize, loaded);
  return { skip: 0, top: size + 1, size };
};

// A card that moved up a page between two requests (one before it was closed, say) arrives twice; keep the first.
export const appendPage = <T>(existing: T[], incoming: T[], keyOf: (item: T) => string | number): T[] => {
  const seen = new Set(existing.map(keyOf));
  return [...existing, ...incoming.filter(item => !seen.has(keyOf(item)))];
};

// A lane's Refresh has landed: the board waits for one lane fewer - but only if it is still the Refresh the board is
// waiting on. A second Refresh before the first lands starts the count again, and the first round's lanes still
// report in, which used to take the count to 0 and un-dim the board while the second round was loading.
export const lanesStillRefreshing = (board: { boardRefresh?: number; boardLanesRefreshing?: number }, finishedRefresh: number) =>
  board.boardRefresh === finishedRefresh
    ? Math.max(0, (board.boardLanesRefreshing ?? 0) - 1)
    : board.boardLanesRefreshing ?? 0;
